import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {searchWikidataProperties} from '../../../utils/api/wikidataProperties';
import {searchKbEntities} from '../../../utils/api/kbEntitySearch';
import {importWikidataEntityToKb, normalizeWikidataQid} from '../../../utils/importWikidataEntityToKb';
import {isLocalEntityRef} from '../../../utils/entityLinks';

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 20;

function normalizeStringValue(value) {
    return typeof value === 'string' ? value : '';
}

function formatPropertyLabelSuggestion(option) {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.propertyLabel || option.propertyId || '';
}

function formatEntityValueSuggestion(option) {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.entityLabel || option.canonicalName || option.entityId || '';
}

/**
 * Create/edit dialog for KB entity properties (label-only UX).
 * Property ID and value entity ID are populated silently when suggestions are selected.
 * Custom property labels and free-text values remain supported.
 */
const PropertyEditorDialog = ({
                                  open,
                                  mode = 'create',
                                  property,
                                  saving,
                                  error,
                                  onCancel,
                                  onSubmit,
                              }) => {
    const [propertyId, setPropertyId] = useState(() => normalizeStringValue(property?.propertyId));
    const [propertyLabel, setPropertyLabel] = useState(() => normalizeStringValue(property?.propertyLabel));
    const [value, setValue] = useState(() => normalizeStringValue(property?.value));
    const [valueEntityId, setValueEntityId] = useState(() => normalizeStringValue(property?.valueEntityId));
    const [valueLabel, setValueLabel] = useState(() => normalizeStringValue(property?.valueLabel));
    const [touched, setTouched] = useState(false);
    const [importingEntity, setImportingEntity] = useState(false);

    const [propertySuggestions, setPropertySuggestions] = useState([]);
    const [propertySearching, setPropertySearching] = useState(false);
    const [propertySearchQuery, setPropertySearchQuery] = useState('');
    const propertyDebounceRef = useRef(null);
    const propertySearchSeqRef = useRef(0);

    const [entitySuggestions, setEntitySuggestions] = useState([]);
    const [entitySearching, setEntitySearching] = useState(false);
    const [entitySearchQuery, setEntitySearchQuery] = useState('');
    const entityDebounceRef = useRef(null);
    const entitySearchSeqRef = useRef(0);

    useEffect(() => {
        if (!open) return;
        setPropertyId(normalizeStringValue(property?.propertyId));
        setPropertyLabel(normalizeStringValue(property?.propertyLabel));
        setValue(normalizeStringValue(property?.value));
        setValueEntityId(normalizeStringValue(property?.valueEntityId));
        setValueLabel(normalizeStringValue(property?.valueLabel));
        setTouched(false);
        setImportingEntity(false);
        setPropertySuggestions([]);
        setPropertySearchQuery('');
        setPropertySearching(false);
        setEntitySuggestions([]);
        setEntitySearchQuery('');
        setEntitySearching(false);
    }, [open, property]);

    useEffect(() => {
        if (!open) return undefined;

        const q = propertySearchQuery.trim();
        if (q.length < 2) {
            setPropertySuggestions([]);
            setPropertySearching(false);
            return undefined;
        }

        clearTimeout(propertyDebounceRef.current);
        propertyDebounceRef.current = setTimeout(async () => {
            const seq = ++propertySearchSeqRef.current;
            setPropertySearching(true);
            try {
                const {results} = await searchWikidataProperties(q, SEARCH_LIMIT);
                if (seq === propertySearchSeqRef.current) {
                    setPropertySuggestions(Array.isArray(results) ? results : []);
                }
            } catch {
                if (seq === propertySearchSeqRef.current) {
                    setPropertySuggestions([]);
                }
            } finally {
                if (seq === propertySearchSeqRef.current) {
                    setPropertySearching(false);
                }
            }
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(propertyDebounceRef.current);
    }, [open, propertySearchQuery]);

    useEffect(() => {
        if (!open) return undefined;

        const q = entitySearchQuery.trim();
        if (q.length < 2) {
            setEntitySuggestions([]);
            setEntitySearching(false);
            return undefined;
        }

        clearTimeout(entityDebounceRef.current);
        entityDebounceRef.current = setTimeout(async () => {
            const seq = ++entitySearchSeqRef.current;
            setEntitySearching(true);
            try {
                const {results} = await searchKbEntities(q, SEARCH_LIMIT);
                if (seq === entitySearchSeqRef.current) {
                    setEntitySuggestions(Array.isArray(results) ? results : []);
                }
            } catch {
                if (seq === entitySearchSeqRef.current) {
                    setEntitySuggestions([]);
                }
            } finally {
                if (seq === entitySearchSeqRef.current) {
                    setEntitySearching(false);
                }
            }
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(entityDebounceRef.current);
    }, [open, entitySearchQuery]);

    const validationErrors = useMemo(() => {
        const nextErrors = {};
        if (!propertyLabel.trim()) {
            nextErrors.propertyLabel = 'Property label is required';
        }
        if (!value.trim()) {
            nextErrors.value = 'Value is required';
        }
        return nextErrors;
    }, [propertyLabel, value]);

    const hasValidationErrors = Object.keys(validationErrors).length > 0;
    const isBusy = saving || importingEntity;

    const applyPropertySuggestion = (option) => {
        if (!option || typeof option === 'string') return;
        if (option.propertyId) {
            setPropertyId(option.propertyId);
        }
        if (option.propertyLabel != null) {
            setPropertyLabel(option.propertyLabel);
        }
    };

    const applyEntitySuggestion = (option) => {
        if (!option || typeof option === 'string') return;
        const entityRef = option.entityRef || option.entityId || option.id || '';
        const entityName = option.entityLabel || option.canonicalName || option.label || entityRef;
        if (entityRef) {
            setValueEntityId(entityRef);
        }
        if (entityName) {
            setValue(entityName);
            setValueLabel(entityName);
        }
    };

    const handlePropertyLabelInputChange = (_, newInputValue, reason) => {
        if (reason === 'reset') return;
        if (reason === 'clear') {
            setPropertyLabel('');
            setPropertyId('');
            setPropertySearchQuery('');
            return;
        }
        if (reason === 'input') {
            setPropertyLabel(newInputValue);
            setPropertySearchQuery(newInputValue);
            // Custom label — drop any previously selected Wikidata P-ID.
            setPropertyId('');
        }
    };

    const handlePropertySelect = (_, newValue) => {
        if (newValue == null) return;
        if (typeof newValue === 'string') {
            setPropertyLabel(newValue);
            setPropertyId('');
            return;
        }
        applyPropertySuggestion(newValue);
    };

    const handleValueInputChange = (_, newInputValue, reason) => {
        if (reason === 'reset') return;
        if (reason === 'clear') {
            setValue('');
            setValueEntityId('');
            setValueLabel('');
            setEntitySearchQuery('');
            return;
        }
        if (reason === 'input') {
            setValue(newInputValue);
            setEntitySearchQuery(newInputValue);
            // Free-text value — drop any previously linked entity ref.
            setValueEntityId('');
            setValueLabel('');
        }
    };

    const handleEntitySelect = (_, newValue) => {
        if (newValue == null) return;
        if (typeof newValue === 'string') {
            setValue(newValue);
            setValueEntityId('');
            setValueLabel('');
            return;
        }
        applyEntitySuggestion(newValue);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setTouched(true);
        if (hasValidationErrors) return;

        const trimmedPropertyId = propertyId.trim();
        const trimmedEntityId = valueEntityId.trim();
        const normalizedQid = normalizeWikidataQid(trimmedEntityId);

        if (normalizedQid && !isLocalEntityRef(trimmedEntityId)) {
            setImportingEntity(true);
            try {
                await importWikidataEntityToKb(normalizedQid);
            } catch {
                // Still save the property link even if hydration fails (rate limits, offline, etc.).
            } finally {
                setImportingEntity(false);
            }
        }

        await onSubmit?.({
            propertyId: trimmedPropertyId || null,
            propertyLabel: propertyLabel.trim(),
            value: value.trim(),
            valueEntityId: trimmedEntityId || null,
            valueLabel: trimmedEntityId && valueLabel.trim() ? valueLabel.trim() : null,
        });
    };

    const renderPropertySuggestionOption = (props, option) => (
        <Box component="li" {...props} key={option.propertyId || formatPropertyLabelSuggestion(option)}>
            <Box>
                <Typography variant="body2">{formatPropertyLabelSuggestion(option)}</Typography>
                {option.propertyId ? (
                    <Typography variant="caption" color="text.secondary">
                        {option.propertyId}
                        {option.description ? ` · ${option.description}` : ''}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );

    const renderEntitySuggestionOption = (props, option) => (
        <Box component="li" {...props} key={option.entityRef || option.entityId || formatEntityValueSuggestion(option)}>
            <Box>
                <Typography variant="body2">
                    {formatEntityValueSuggestion(option)}
                    {option.local ? ' (Custom)' : ''}
                </Typography>
                {(option.entityRef || option.entityId || option.description) ? (
                    <Typography variant="caption" color="text.secondary">
                        {[option.entityRef || option.entityId, option.description].filter(Boolean).join(' · ')}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );

    return (
        <Dialog open={open} onClose={isBusy ? undefined : onCancel} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>{mode === 'edit' ? 'Edit property' : 'Add property'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{pt: 1}}>
                        {error && <Alert severity="error">{error.message}</Alert>}

                        <Autocomplete
                            freeSolo
                            options={propertySuggestions}
                            filterOptions={(opts) => opts}
                            getOptionLabel={formatPropertyLabelSuggestion}
                            isOptionEqualToValue={(option, val) => {
                                const label = typeof val === 'string' ? val : val?.propertyLabel;
                                return Boolean(option?.propertyLabel) && option.propertyLabel === label;
                            }}
                            loading={propertySearching}
                            disabled={isBusy}
                            onChange={handlePropertySelect}
                            renderOption={renderPropertySuggestionOption}
                            value={propertyLabel}
                            inputValue={propertyLabel}
                            onInputChange={handlePropertyLabelInputChange}
                            fullWidth
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    autoFocus
                                    label="Property label"
                                    placeholder="e.g. instance of"
                                    error={touched && !!validationErrors.propertyLabel}
                                    helperText={
                                        touched
                                            ? validationErrors.propertyLabel
                                            : 'Pick an existing property or type a new label'
                                    }
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {propertySearching ? <CircularProgress color="inherit" size={16}/> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        <Autocomplete
                            freeSolo
                            options={entitySuggestions}
                            filterOptions={(opts) => opts}
                            getOptionLabel={formatEntityValueSuggestion}
                            isOptionEqualToValue={(option, val) => {
                                const label = typeof val === 'string' ? val : val?.entityLabel;
                                return Boolean(option?.entityLabel) && option.entityLabel === label;
                            }}
                            loading={entitySearching}
                            disabled={isBusy}
                            onChange={handleEntitySelect}
                            renderOption={renderEntitySuggestionOption}
                            value={value}
                            inputValue={value}
                            onInputChange={handleValueInputChange}
                            fullWidth
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Value"
                                    placeholder="e.g. human or Douglas Adams"
                                    error={touched && !!validationErrors.value}
                                    helperText={
                                        touched
                                            ? validationErrors.value
                                            : 'Plain text, or search KB / Wikidata entities'
                                    }
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {entitySearching || importingEntity
                                                    ? <CircularProgress color="inherit" size={16}/>
                                                    : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCancel} disabled={isBusy}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={isBusy}>
                        {importingEntity
                            ? 'Importing entity...'
                            : saving
                                ? 'Saving...'
                                : mode === 'edit'
                                    ? 'Save changes'
                                    : 'Create property'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default PropertyEditorDialog;
